/**
 * `skaile source manifest publish` / `yank` — the curator-publish flow. Takes a
 * locally-curated sidecar manifest (over a foreign public GitHub repo) and:
 *
 *   1. Pushes the curated `skaile.manifest.yaml` to the curator's OWN public
 *      "sidecar" repo (bytes stay in the foreign source; the sidecar holds only
 *      the manifest pointing at it).
 *   2. Registers the foreign source + the sidecar (url + pinned commit) with the
 *      store, syncs it, and publishes each curated asset.
 *
 * `yank` is the inverse: unpublish each currently-published asset of the source.
 *
 * The store REST surface is reached through the injectable {@link PublisherClient}
 * (default HTTP client); git side-effects go through an injectable `spawn`. Both
 * seams keep the pure helpers + orchestration unit/integration-testable without a
 * live store or a real remote.
 *
 * @docLink cli/commands/source#manifest-publish
 */

import { spawnSync } from "node:child_process";
import { existsSync, mkdirSync, readFileSync, rmSync, writeFileSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join } from "node:path";
import {
  decodeSkaileManifest,
  encodeSkaileManifest,
  type SkPublishManifest,
  sourceCacheKey,
} from "@skaile/workspaces/core";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";
import { makeHttpPublisherClient, type PublisherClient } from "../store-publisher.ts";
import { isStoreAuthenticated } from "../store-client.ts";
import { bareName, publishToStore, refFor } from "./publish-core.ts";
import { sourceClonePath } from "./source.ts";
import { resolveSourceArg } from "./source-manifest.ts";

/** The fixed filename a sidecar repo carries at its root. */
const SIDECAR_MANIFEST_FILENAME = "skaile.manifest.yaml";

/** Injectable spawn contract — the subset of `spawnSync` these helpers use. */
type SpawnLike = typeof spawnSync;

/**
 * Return a copy of `manifest` normalized for publication under `namespace`:
 * the top-level `publisher` is set to `namespace` and every per-asset
 * `publisher` override is removed so all assets inherit the top-level one.
 * Pure — the input is not mutated.
 */
export function normalizePublishedManifest(
  manifest: SkPublishManifest,
  namespace: string,
): SkPublishManifest {
  const assets = (manifest.assets ?? []).map((a) => {
    const { publisher: _drop, ...rest } = a;
    return rest;
  });
  return { ...manifest, publisher: namespace, assets };
}

// ── Sidecar-repo association (~/.skaile/settings.json) ────────────────────────

/**
 * Path to the user settings file — the same `~/.skaile/settings.json` the store
 * client reads tokens from. Resolves the `.skaile` root exactly as `getStoreRoot`
 * does (`SKAILE_HOME` when set — it IS the `.skaile` dir — else `~/.skaile`), so
 * the sidecar association lives beside the store manifests under one convention
 * and tests isolate through the same `SKAILE_HOME` seam.
 */
function settingsFile(): string {
  const root = process.env.SKAILE_HOME ?? join(homedir(), ".skaile");
  return join(root, "settings.json");
}

function readSettings(): Record<string, any> {
  const file = settingsFile();
  if (!existsSync(file)) return {};
  try {
    return JSON.parse(readFileSync(file, "utf-8"));
  } catch {
    return {};
  }
}

/**
 * The sidecar repo URL previously associated with a source `slug`, if any.
 * Reads `settings.sidecarRepos?.[slug]?.repo`.
 */
export function getSidecarRepo(slug: string): string | undefined {
  const settings = readSettings();
  return settings.sidecarRepos?.[slug]?.repo;
}

/**
 * Persist the sidecar repo URL for a source `slug`, preserving every other
 * settings key. Stored as an object (`{ repo }`) so a `path` (or other fields)
 * can be added later without a schema migration.
 */
export function saveSidecarRepo(slug: string, repo: string): void {
  const file = settingsFile();
  mkdirSync(dirname(file), { recursive: true });
  const settings = readSettings();
  const sidecarRepos = { ...(settings.sidecarRepos ?? {}) };
  sidecarRepos[slug] = { ...(sidecarRepos[slug] ?? {}), repo };
  settings.sidecarRepos = sidecarRepos;
  writeFileSync(file, JSON.stringify(settings, null, 2), "utf-8");
}

// ── Sidecar push ──────────────────────────────────────────────────────────────

/**
 * Run a git command in `cwd` via the injected spawn, throwing with the captured
 * stderr on failure. Returns trimmed stdout.
 */
function git(spawn: SpawnLike, cwd: string, args: string[]): string {
  const res = spawn("git", ["-C", cwd, ...args], { encoding: "utf-8" });
  if (res.status !== 0) {
    const stderr = (res.stderr ?? "").toString().trim();
    throw new Error(`git ${args.join(" ")} failed: ${stderr || `exit ${res.status}`}`);
  }
  return (res.stdout ?? "").toString().trim();
}

/**
 * Clone the curator's sidecar repo, write the curated manifest at its root, and
 * commit + push. Returns the pushed commit sha (used to pin the store source).
 *
 * Idempotent re-publish: when the manifest is unchanged `git commit` fails with
 * "nothing to commit" — we swallow that and return the current HEAD so a no-op
 * re-run still yields a valid pin. Any push/auth failure surfaces as a thrown
 * Error carrying git's stderr.
 */
export function pushSidecarManifest(opts: {
  sidecarRepoUrl: string;
  manifestText: string;
  spawn?: SpawnLike;
}): { commitSha: string } {
  const spawn = opts.spawn ?? spawnSync;
  // Dedicated dir next to the source clone cache; wipe any stale clone first so
  // a re-publish always starts from a clean shallow checkout of the remote.
  const cloneDir = `${sourceClonePath(sourceCacheKey(opts.sidecarRepoUrl))}-sidecar-push`;
  rmSync(cloneDir, { recursive: true, force: true });
  mkdirSync(dirname(cloneDir), { recursive: true });

  // The clone is scratch — always remove it afterwards (success or failure) so a
  // failed push never leaves a stale checkout in the cache dir.
  try {
    const cloneRes = spawn("git", ["clone", "--depth", "1", opts.sidecarRepoUrl, cloneDir], {
      encoding: "utf-8",
    });
    if (cloneRes.status !== 0) {
      const stderr = (cloneRes.stderr ?? "").toString().trim();
      throw new Error(
        `git clone ${opts.sidecarRepoUrl} failed: ${stderr || `exit ${cloneRes.status}`}`,
      );
    }

    writeFileSync(join(cloneDir, SIDECAR_MANIFEST_FILENAME), opts.manifestText, "utf-8");
    git(spawn, cloneDir, ["add", SIDECAR_MANIFEST_FILENAME]);

    // Fixed bot identity via -c so the commit succeeds regardless of the curator's
    // git config (the sidecar push is a machine action, not authored work).
    const commitRes = spawn(
      "git",
      [
        "-C",
        cloneDir,
        "-c",
        "user.email=publish@skaile.store",
        "-c",
        "user.name=skaile",
        "commit",
        "-m",
        "chore(sidecar): publish curated manifest",
      ],
      { encoding: "utf-8" },
    );
    if (commitRes.status !== 0) {
      const out = `${commitRes.stdout ?? ""}${commitRes.stderr ?? ""}`;
      // Nothing changed → idempotent re-publish; keep the existing HEAD.
      if (!/nothing to commit/i.test(out)) {
        throw new Error(`git commit failed: ${out.trim() || `exit ${commitRes.status}`}`);
      }
    } else {
      // `push origin HEAD` (not a bare `push`) so a fresh sidecar repo with an
      // unborn/renamed default branch still gets its branch created on the remote.
      git(spawn, cloneDir, ["push", "origin", "HEAD"]);
    }

    return { commitSha: git(spawn, cloneDir, ["rev-parse", "HEAD"]) };
  } finally {
    rmSync(cloneDir, { recursive: true, force: true });
  }
}

// ── Orchestration ───────────────────────────────────────────────────────────

/**
 * `source manifest publish <src>` — publish a curated sidecar manifest to the
 * store. See the module header for the full flow. Store calls go through
 * `client` (default HTTP); git side-effects through `spawn` (default
 * `spawnSync`). The auth gate is enforced only for the default HTTP client so
 * injected fakes bypass it.
 */
export async function runPublish(
  arg: string,
  opts: {
    projectDir: string;
    as?: string;
    sidecarRepo?: string;
    ref?: string;
    dryRun?: boolean;
  },
  client?: PublisherClient,
  spawn: SpawnLike = spawnSync,
): Promise<void> {
  const injected = client !== undefined;
  const pub = client ?? makeHttpPublisherClient();

  const { url, slug, manifestFile } = resolveSourceArg(arg, opts.projectDir);

  if (!existsSync(manifestFile)) {
    logErr(
      `No curated manifest for "${slug}" at ${S.dim(manifestFile)} — ` +
        "run `skaile source manifest init` first.",
    );
    process.exit(1);
  }

  const { manifest, diagnostics } = decodeSkaileManifest(readFileSync(manifestFile, "utf-8"));
  if (diagnostics.some((d) => d.severity === "error")) {
    for (const d of diagnostics.filter((x) => x.severity === "error")) {
      logErr(`${d.code}: ${d.message}`);
    }
    logErr(
      "The curated manifest has errors — fix them with `skaile source manifest edit` / " +
        "`verify` before publishing.",
    );
    process.exit(1);
  }

  // A real publish makes store calls (+ `me()` when `--as` is omitted), so it
  // needs a token. `--dry-run` is an offline preview: never gate it on auth.
  const authed = injected || isStoreAuthenticated();
  if (!opts.dryRun && !authed) {
    logErr("Not signed in — run `skaile store login` first.");
    process.exit(1);
  }

  // Namespace: `--as` wins; else the caller's verified identity. Prefer the
  // GitHub handle (`githubLogin`) over the store login (`username`) — for a user
  // whose login differs from their GitHub handle the login namespace may be
  // reserved (matches the CLI's `resolveLibraryPublisher` precedence). In an
  // unauthenticated dry-run we cannot call `me()`, so show a placeholder.
  let namespace: string;
  if (opts.as) namespace = opts.as;
  else if (authed) {
    const me = await pub.me();
    // `||` not `??`: an empty-string githubLogin (partially-connected account) is
    // not a usable namespace — fall back to the login rather than publish under "".
    namespace = me.githubLogin || me.username;
  } else namespace = "<your store username>";

  const normalized = normalizePublishedManifest(manifest, namespace);
  const publishedText = encodeSkaileManifest(normalized);

  const wouldBeRefs = (normalized.assets ?? []).map((a) =>
    refFor(a.kind, namespace, a.name, a.version ?? normalized.version),
  );

  if (opts.dryRun) {
    const sidecar = opts.sidecarRepo ?? getSidecarRepo(slug) ?? "<none — pass --sidecar-repo>";
    logInfo(`Publish plan for ${S.bold(slug)}:`);
    logInfo(`  namespace:    ${namespace}`);
    logInfo(`  sidecar repo: ${sidecar}`);
    logInfo(`  source ref:   ${opts.ref ?? "main"}`);
    logInfo(`  would publish ${wouldBeRefs.length} asset(s):`);
    for (const r of wouldBeRefs) logInfo(`    ${r}`);
    return;
  }

  const sidecarRepoUrl = opts.sidecarRepo ?? getSidecarRepo(slug);
  if (!sidecarRepoUrl) {
    logErr(
      "No sidecar repo associated with this source. Pass " +
        `${S.cmd("--sidecar-repo https://github.com/<you>/<repo>")} — it must be a ` +
        "PUBLIC GitHub repo you own; the curated manifest is committed there and the " +
        "store pins to that commit.",
    );
    process.exit(1);
  }
  if (opts.sidecarRepo) saveSidecarRepo(slug, sidecarRepoUrl);

  const { commitSha } = pushSidecarManifest({ sidecarRepoUrl, manifestText: publishedText, spawn });
  logOk(`Pushed curated manifest to ${S.dim(sidecarRepoUrl)} @ ${commitSha.slice(0, 10)}.`);

  // The FOREIGN repo's ref to ingest (branch/sha). Defaults to `main`.
  const sourceRef = opts.ref ?? "main";

  const { published } = await publishToStore({
    sourceUrl: url,
    ref: sourceRef,
    sidecar: { url: sidecarRepoUrl, ref: commitSha },
    manifest: normalized,
    client: pub,
  });

  if (published.length > 0) {
    logInfo(`Consumers can now install them with \`skaile add ${published[0]}\`.`);
  }
}

/**
 * `source manifest yank <src>` — unpublish the source's published assets (all,
 * or the single asset matching `opts.asset`'s name). Auth-gated like publish.
 */
export async function runYank(
  arg: string,
  opts: { projectDir: string; asset?: string },
  client?: PublisherClient,
): Promise<void> {
  const injected = client !== undefined;
  const pub = client ?? makeHttpPublisherClient();

  const { url, slug } = resolveSourceArg(arg, opts.projectDir);

  if (!injected && !isStoreAuthenticated()) {
    logErr("Not signed in — run `skaile store login` first.");
    process.exit(1);
  }

  const rows = await pub.sourcesList();
  const source = rows.find((r) => r.url === url);
  if (!source) {
    logErr(`Source "${slug}" is not registered in the store — nothing to yank.`);
    process.exit(1);
  }

  // Optionally narrow to a single asset by name (accept a bare name or a ref).
  const targetName = opts.asset ? bareName(opts.asset) : undefined;
  const candidates = (await pub.sourceCandidates(source.id)).filter(
    (c) => c.published && (!targetName || c.name === targetName),
  );
  if (candidates.length === 0) {
    logWarn("No published assets matched — nothing to yank.");
    return;
  }

  const yanked: string[] = [];
  for (const c of candidates) {
    try {
      await pub.assetUnpublish(c.id);
      yanked.push(`${c.kind}:${c.name}`);
    } catch (err) {
      logWarn(
        `Failed to yank ${c.kind}:${c.name}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  logOk(`Yanked ${yanked.length} asset(s):`);
  for (const r of yanked) logInfo(`  ${r}`);
}
